import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { createTicker } from "@/lib/ticker";

const LiveCoachContext = createContext(null);

// Cadence at which queued real-vision cues are spoken aloud.
export const LEVELS = {
    minimal: { interval: 16000, label: "Minimal", desc: "Only critical callouts" },
    balanced: { interval: 10000, label: "Balanced", desc: "Steady guidance" },
    intense: { interval: 6000, label: "Intense", desc: "Constant coaching" },
};

export const CAT_COLOR = {
    "Threat": "#FF2D55",
    "Positioning": "#00C2FF",
    "Awareness": "#FFB000",
    "Gameplay Habits": "#FF3366",
    "Match Coaching": "#00E676",
    "Post-Fight": "#14B8A6",
};

const QUEUE_CAP = 6;

export function LiveCoachProvider({ children }) {
    const [enabled, setEnabledState] = useState(false);
    const [level, setLevelState] = useState("balanced");
    const [voice, setVoiceState] = useState(true);
    const [voiceUri, setVoiceUriState] = useState("");
    const [volume, setVolumeState] = useState(1);
    const [voices, setVoices] = useState([]);
    const [model, setModel] = useState("gemini-3-flash");
    const [game, setGame] = useState("warzone");
    const [monitoring, setMonitoring] = useState(false);
    const [current, setCurrent] = useState(null);
    const [history, setHistory] = useState([]);
    const [analyzing, setAnalyzing] = useState(false);

    const queueRef = useRef([]);
    const tickRef = useRef(null);
    const voiceRef = useRef(true);
    const voiceUriRef = useRef("");
    const volumeRef = useRef(1);
    const enabledRef = useRef(false);
    const monitoringRef = useRef(false);

    // Keep "latest value" refs in sync (read inside stable async callbacks like ingestCues/speak).
    useEffect(() => {
        voiceRef.current = voice;
        voiceUriRef.current = voiceUri;
        volumeRef.current = volume;
        enabledRef.current = enabled;
        monitoringRef.current = monitoring;
    });

    // Load persisted live-coach prefs.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/settings");
                if (typeof data.live_coach_enabled === "boolean") setEnabledState(data.live_coach_enabled);
                if (data.live_coach_level) setLevelState(data.live_coach_level);
                if (typeof data.live_coach_voice === "boolean") setVoiceState(data.live_coach_voice);
                if (typeof data.live_coach_voice_uri === "string") setVoiceUriState(data.live_coach_voice_uri);
                if (typeof data.live_coach_volume === "number") setVolumeState(data.live_coach_volume);
            } catch { /* ignore */ }
        })();
    }, []);

    // Enumerate browser TTS voices (async-populated in Chrome).
    useEffect(() => {
        if (!("speechSynthesis" in window)) return;
        const load = () => {
            const list = window.speechSynthesis.getVoices() || [];
            if (list.length) setVoices(list);
        };
        load();
        window.speechSynthesis.onvoiceschanged = load;
        return () => { window.speechSynthesis.onvoiceschanged = null; };
    }, []);

    // Poll live monitoring/session state — the coach only speaks while gameplay is being watched.
    useEffect(() => {
        let alive = true;
        const poll = async () => {
            try {
                const { data } = await api.get("/session");
                if (!alive) return;
                setMonitoring(!!data.monitoring);
                if (data.current_game?.id) setGame(data.current_game.id);
                else if (typeof data.current_game === "string" && data.current_game) setGame(data.current_game);
            } catch { /* ignore */ }
        };
        poll();
        const t = setInterval(poll, 5000);
        // Instant flip when Live Capture starts/stops monitoring (no waiting for the poll).
        const onSession = (e) => {
            const d = e.detail || {};
            if (typeof d.monitoring === "boolean") setMonitoring(d.monitoring);
            if (d.game) setGame(d.game);
        };
        window.addEventListener("ww:session", onSession);
        return () => { alive = false; clearInterval(t); window.removeEventListener("ww:session", onSession); };
    }, []);

    const persist = (patch) => { api.put("/settings", patch).catch(() => {}); };

    const speak = useCallback((text) => {
        if (!("speechSynthesis" in window)) return;
        try {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(text);
            u.rate = 1.07; u.pitch = 1.0; u.volume = volumeRef.current;
            if (voiceUriRef.current) {
                const v = window.speechSynthesis.getVoices().find((x) => x.voiceURI === voiceUriRef.current);
                if (v) u.voice = v;
            }
            window.speechSynthesis.speak(u);
        } catch { /* ignore */ }
    }, []);

    // Speak a sample line with the currently-selected voice + volume (used by the "Test" button).
    const testVoice = useCallback(() => {
        if (!("speechSynthesis" in window)) return;
        speak("Coach online. Hold that angle and watch your left flank.");
    }, [speak]);

    const beep = useCallback(() => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            const ctx = new Ctx();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = "sine"; o.frequency.value = 880;
            const vol = Math.max(0.02, 0.07 * volumeRef.current);
            g.gain.setValueAtTime(0.0001, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + 0.01);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
            o.start(); o.stop(ctx.currentTime + 0.2);
            o.onended = () => ctx.close();
        } catch { /* ignore */ }
    }, []);

    const present = useCallback((cue) => {
        const stamped = { ...cue, key: cue.key || `${Date.now()}-${Math.random()}`, at: new Date() };
        setCurrent(stamped);
        setHistory((h) => [stamped, ...h].slice(0, 15));
        if (voiceRef.current) speak(cue.text); else beep();
    }, [speak, beep]);

    // Real cues from the live vision pipeline. Threat/enemy + high-priority cues are spoken INSTANTLY
    // (no queue lag); normal coaching is queued and paced by the coaching level.
    const ingestCues = useCallback((cues) => {
        // HARD GATE: when the Live AI Coach toggle is OFF (or monitoring stopped), the coach must stay
        // completely silent — no instant threat callouts, no queued cues. Monitoring still runs the
        // vision pipeline for clips/anomalies/matches, but the coaching voice/overlay is off.
        if (!enabledRef.current || !monitoringRef.current) return;
        const valid = (cues || []).filter((c) => c && c.text);
        if (!valid.length) return;
        const urgent = valid.filter((c) => c.threat || c.priority === "high");
        const normal = valid.filter((c) => !(c.threat || c.priority === "high"));
        if (urgent.length) {
            // Speak the most urgent immediately; show it now.
            present(urgent[0]);
            // Queue any remaining urgent ahead of normal coaching.
            const rest = urgent.slice(1).map((c) => ({ ...c, key: `${Date.now()}-${Math.random()}` }));
            queueRef.current = [...rest, ...queueRef.current].slice(0, QUEUE_CAP);
        }
        if (normal.length) {
            const stamped = normal.map((c) => ({ ...c, key: `${Date.now()}-${Math.random()}` }));
            queueRef.current = [...queueRef.current, ...stamped].slice(-QUEUE_CAP);
        }
    }, [present]);

    // Called when a frame reports a silent game state (lobby/menu/dead) — drop pending chatter.
    const clearQueue = useCallback(() => { queueRef.current = []; }, []);

    const emitNext = useCallback(() => {
        if (queueRef.current.length === 0) return; // wait for next real frame
        present(queueRef.current.shift());
    }, [present]);
    const emitNextRef = useRef(() => {});
    useEffect(() => { emitNextRef.current = emitNext; });
    const fire = useCallback(() => emitNextRef.current(), []);

    useEffect(() => {
        const active = enabled && monitoring;
        if (!active) {
            if (tickRef.current) { tickRef.current.stop(); tickRef.current = null; }
            if ("speechSynthesis" in window) window.speechSynthesis.cancel();
            queueRef.current = [];
            setCurrent(null);
            return;
        }
        const start = setTimeout(fire, 1200);
        // Background-safe ticker (Web Worker) so cues keep speaking when the tab isn't focused.
        tickRef.current = createTicker(LEVELS[level]?.interval || 10000, fire);
        return () => {
            clearTimeout(start);
            if (tickRef.current) { tickRef.current.stop(); tickRef.current = null; }
        };
    }, [enabled, monitoring, level, fire]);

    const setEnabled = (v) => {
        setEnabledState(v);
        persist({ live_coach_enabled: v });
        if (!v) { setCurrent(null); queueRef.current = []; if ("speechSynthesis" in window) window.speechSynthesis.cancel(); }
    };
    const setLevel = (v) => { setLevelState(v); persist({ live_coach_level: v }); };
    const setVoice = (v) => { setVoiceState(v); persist({ live_coach_voice: v }); if (!v && "speechSynthesis" in window) window.speechSynthesis.cancel(); };
    const setVoiceUri = (v) => { setVoiceUriState(v); persist({ live_coach_voice_uri: v }); };
    const setVolume = (v) => { setVolumeState(v); persist({ live_coach_volume: v }); };

    return (
        <LiveCoachContext.Provider value={{
            enabled, setEnabled, level, setLevel, voice, setVoice, model, setModel,
            voiceUri, setVoiceUri, volume, setVolume, voices, testVoice,
            game, setGame, current, history, loading: analyzing, setAnalyzing,
            monitoring, active: enabled && monitoring, ingestCues, clearQueue,
        }}>
            {children}
        </LiveCoachContext.Provider>
    );
}

export const useLiveCoach = () => useContext(LiveCoachContext);
